import { AuthService } from './authService';
import type { LabelPlaceholder, TableSchema } from '../types/labelDesign';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Service for henting av plassholder-katalogen fra backend.
 * Katalogen endrer seg sjelden, så vi cacher per (companyNo, reportType)-kombinasjon
 * for hele siden-levetiden. Refresh av siden henter på nytt.
 */
class LabelPlaceholderService {
  private cache = new Map<string, Promise<LabelPlaceholder[]>>();
  private tableSchemaCache = new Map<string, Promise<TableSchema[]>>();

  private cacheKey(companyNo: string, reportType?: number): string {
    return `${companyNo}::${reportType ?? 'all'}`;
  }

  /**
   * Henter plassholdere fra backend. Hvis reportType er satt, filtreres listen
   * til kun relevante plassholdere for den rapporttypen.
   */
  async getPlaceholders(companyNo: string, reportType?: number): Promise<LabelPlaceholder[]> {
    const key = this.cacheKey(companyNo, reportType);
    const cached = this.cache.get(key);
    if (cached) return cached;

    const query = reportType !== undefined ? `?reportType=${reportType}` : '';
    const promise = AuthService.authenticatedGet(
      `${API_BASE_URL}/labeldesigner/${companyNo}/placeholders${query}`
    ).then(async (response) => {
      if (!response.ok) {
        const body = await response.text();
        throw new Error(`Kunne ikke hente plassholdere (${response.status}): ${body || response.statusText}`);
      }
      return (await response.json()) as LabelPlaceholder[];
    });

    // Cache selve promiset så samtidige kall ikke forårsaker duplikate requests
    this.cache.set(key, promise);

    // Hvis det feiler, fjern fra cachen slik at neste forsøk går ut igjen
    promise.catch(() => this.cache.delete(key));

    return promise;
  }

  /**
   * Henter skjema for tabellene som kan brukes som plassholdere (Items, Orders, OrderLine,
   * POrders, POrderLine, StcBin). Backend cacher per tenant, men vi cacher også på frontend
   * for å unngå dupliserte requests i samme sesjon.
   */
  async getTableSchemas(companyNo: string): Promise<TableSchema[]> {
    const cached = this.tableSchemaCache.get(companyNo);
    if (cached) return cached;

    const promise = AuthService.authenticatedGet(
      `${API_BASE_URL}/labeldesigner/${companyNo}/placeholders/tables`
    ).then(async (response) => {
      if (!response.ok) {
        const body = await response.text();
        throw new Error(`Kunne ikke hente tabellskjema (${response.status}): ${body || response.statusText}`);
      }
      return (await response.json()) as TableSchema[];
    });

    this.tableSchemaCache.set(companyNo, promise);
    promise.catch(() => this.tableSchemaCache.delete(companyNo));

    return promise;
  }

  /** Tøm cachen (f.eks. ved utlogging). */
  clearCache(): void {
    this.cache.clear();
    this.tableSchemaCache.clear();
  }
}

export default new LabelPlaceholderService();
